import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, FieldArray } from 'redux-form';
import { connect } from 'react-redux';
import { get } from 'lodash';
import SelectField from '../../fields/SelectField/SelectField';
import TextField from '../../fields/TextField/TextField';
import ToggleField from '../../fields/ToggleField/ToggleField';
import NumberField from '../../fields/NumberField/NumberField';
import AddOnField from '../../fields/AddOnField/AddOnField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import { CREATE_ITEM_FORM } from '../../constants/formNames';
import { AddFeesIntl } from '../../fieldSets/AddFees/add-fees';
import { VatAddFeesIntl } from '../../fieldSets/AddFees/vat-add-fees';
import { AddTaxesIntl } from '../../fieldSets/AddTaxes/add-taxes';
import { VatAddTaxesIntl } from '../../fieldSets/AddTaxes/vat-add-taxes';
import { PageSpinner } from 'shared-components/decorators/spinner';
import submitCreateAddOnForm from '../../submit/submitCreateAddOnForm';
import validateCreateItemForm from '../../validate/validateCreateItemForm';
import RequiredLabel from '../../../../../elements-inputs/RequiredLabel';
import AddOnPhoto from '../../../../../elements-forms/AddOnPhoto';
import { FormattedMessage, defineMessages, injectIntl, intlShape } from 'react-intl';
import SelectFieldHOC from '../../fields/SelectField/SelectFieldHOC';
import { getTooltipData, Tooltip } from '../../../../../elements-tooltip/tooltip';

const SelectFiledWithHOC = SelectFieldHOC(SelectField);

const CreateItemForm = ({
  handleSubmit = (e) => { e.preventDefault(); },
  priceOptions = [],
  subcategoryOptions = [],
  categoryOptions = [],
  currency = '',
  handleCategoryChange = null,
  addOnFeeValues = [],
  isFetching = false,
  fromManageAddons = false,
  handlePhotoChange = null,
  addOnImgId = '',
  isEditAddOnModal,
  handleRemovePhotoClick,
  iosWebView = false,
  intl,
  isVatEnabled = false,
  intlAddOnTaxTypes = [],
  intlCollectVatCopy = [],
  addOnTaxValues = [],
  formValues = {},
  inventoryError
}) => {
  const translationMessages = defineMessages({
    yes: {
      id: 'hosting.create_item_form.yes_label',
      defaultMessage: 'Yes'
    },
    no: {
      id: 'hosting.create_item_form.no_label',
      defaultMessage: 'No'
    },
    itemNamePlaceholder: {
      id: 'hosting.create_item_form.item_name_placeholder',
      defaultMessage: 'eg: Lunch Buffet'
    },
    selectPlaceholder: {
      id: 'hosting.create_item_form.select_placeholder',
      defaultMessage: 'Select'
    },
    categoryErrorMessage: {
      id: 'hosting.create_item_form.category_error_message',
      defaultMessage: 'Please select a category'
    },
    subcategoryErrorMessage: {
      id: 'hosting.create_item_form.subcategory_error_message',
      defaultMessage: 'Please select a subcategory'
    },
    descriptionPlaceholder: {
      id: 'hosting.create_item_form.description_placeholder',
      defaultMessage: 'eg: Includes deli sandwiches, chips, coffee & dessert'
    },
    priceErrorMessage: {
      id: 'hosting.create_item_form.price_error_message',
      defaultMessage: 'Please make a selection'
    },
    perItem: {
      id: 'hosting.price_options.per_item',
      defaultMessage: 'per item'
    },
    maxQuantityPlaceholder: {
      id: 'hosting.create_item_form.max_quantity_placeholder',
      defaultMessage: 'Optional'
    }
  });

  return (
    <div>
      {
        isFetching ?
          <PageSpinner additionalStyle="gray" />
          :
          <form className="create-item-form" onSubmit={handleSubmit}>
            {
              !iosWebView &&
                <AddOnPhoto
                  addOnImgId={addOnImgId}
                  handlePhotoChange={handlePhotoChange}
                  handleRemovePhotoClick={handleRemovePhotoClick}
                  isEditAddOnModal={isEditAddOnModal}
                  intl={intl}
                />
            }

            <div className="title-section">
              <RequiredLabel className="label" htmlFor="label"><FormattedMessage id="hosting.create_item_form.item_name_label" defaultMessage="Item name"/></RequiredLabel>
              <Field
                name="label"
                component={TextField}
                className="form-fields"
                attributes={{ 'data-testing-id': 'title-field' }}
                placeholder={intl.formatMessage(translationMessages.itemNamePlaceholder)}
              />
            </div>

            <div className="category-section">
              <RequiredLabel className="label" htmlFor="category"><FormattedMessage id="hosting.create_item_form.category_label" defaultMessage="Category"/></RequiredLabel>
              <Field
                component={SelectField}
                name="category"
                errorMessage={intl.formatMessage(translationMessages.categoryErrorMessage)}
                className="form-fields"
                placeholder={intl.formatMessage(translationMessages.selectPlaceholder)}
                options={categoryOptions}
                maxMenuHeight={180}
                onChange={handleCategoryChange}
                attributes={{ 'data-testing-id': 'category-field' }}
              />
            </div>

            <div className="subcategory-section">
              <RequiredLabel className="label" htmlFor="subcategory"><FormattedMessage id="hosting.create_item_form.subcategory_label" defaultMessage="Subcategory"/></RequiredLabel>
              <Field
                component={SelectFiledWithHOC}
                name="subcategory"
                errorMessage={intl.formatMessage(translationMessages.subcategoryErrorMessage)}
                className="form-fields"
                placeholder={intl.formatMessage(translationMessages.selectPlaceholder)}
                options={subcategoryOptions}
                maxMenuHeight={180}
                attributes={{ 'data-testing-id': 'subcategory-field' }}
                defaultValue={formValues.category === 'other' ? 'other-other' : null}
              />
            </div>

            <div className="description-section">
              <RequiredLabel className="label" htmlFor="description"><FormattedMessage id="hosting.create_item_form.description_label" defaultMessage="Description"/></RequiredLabel>
              <Field
                name="description"
                component={TextAreaField}
                className="form-fields"
                placeholder={intl.formatMessage(translationMessages.descriptionPlaceholder)}
                maxHeight={92}
                maxLength={2000}
                attributes={{ 'data-testing-id': 'description-field' }}
              />
            </div>

            <div className="price-section">
              <RequiredLabel className="label" htmlFor="unit_cost"><FormattedMessage id="hosting.create_item_form.price_label" defaultMessage="Price"/></RequiredLabel>
              <div className="form-fields">
                <div className="flex">
                  <div className="flex-basis-60 margin-md-right">
                    <Field
                      name="unit_cost"
                      component={AddOnField}
                      isCurrency
                      label={currency}
                      leftLabel
                      fieldType="number"
                      className="form-fields price-field"
                      attributes={{ 'data-testing-id': 'price-field' }}
                    />
                  </div>
                  <div className="flex-basis-40">
                    <Field
                      component={SelectField}
                      name="unit_cost_basis"
                      className="form-fields"
                      options={priceOptions}
                      errorMessage={intl.formatMessage(translationMessages.priceErrorMessage)}
                      defaultValue={{ value: 'per_item', label: intl.formatMessage(translationMessages.perItem) }}
                      attributes={{ 'data-testing-id': 'unit-cost-basis-field' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="max-quantity-section">
              <label className="label" htmlFor="count"><FormattedMessage id="hosting.create_item_form.max_quantity_label" defaultMessage="Max quantity"/></label>
              <div className="form-fields">
                <div className="flex">
                  <div className="flex-basis-60 margin-md-right">
                    <Field
                      name="count"
                      component={NumberField}
                      attributes={{ 'data-testing-id': 'quantity-field' }}
                      placeholder={intl.formatMessage(translationMessages.maxQuantityPlaceholder)}
                    />
                  </div>
                  <div className="flex-basis-40" />
                </div>
              </div>
            </div>

            <div className="tax-section">
              <label className="label flex middle" htmlFor="taxes" style={{ gap: 4 }}>
                <div className="capitalize-first-letter">
                  <FormattedMessage id="hosting.create_item_form.tax_label" defaultMessage="Tax"/>
                </div>
                <Tooltip tooltip={getTooltipData().ADD_ONS_TAX} />
              </label>
              <FieldArray
                name="taxes"
                component={isVatEnabled ? VatAddTaxesIntl : AddTaxesIntl}
                classnames="form-fields"
                addAddtlTaxes
                intlAddOnTaxTypes={intlAddOnTaxTypes}
                intlCollectVatCopy={intlCollectVatCopy}
                addOnTaxValues={addOnTaxValues}
                taxError={inventoryError}
              />
            </div>

            <div className="fees-section">
              <label className="label margin-sm-right" htmlFor="fees">
                <FormattedMessage id="hosting.create_item_form.fees_label" defaultMessage="Fees"/>
              </label>
              <span className="text-small text-gray-600">
                (<FormattedMessage id="hosting.add_fees.gratuity_delivery_text" defaultMessage="Gratuity, delivery, etc."/>)
              </span>
              <FieldArray
                name="fees"
                currency={currency}
                component={isVatEnabled ? VatAddFeesIntl : AddFeesIntl}
                classnames="form-fields"
                addOnFeeValues={addOnFeeValues}
                addAddtlFees
              />
            </div>

            <div className="listing-visibility-section">
              <RequiredLabel className="label" htmlFor="visible_on_listing"><FormattedMessage id="hosting.create_item_form.show_on_listings_label" defaultMessage="Show on listings?"/></RequiredLabel>
              <Field
                name="visible_on_listing"
                component={ToggleField}
                attributes={{ 'data-testing-id': 'toggle-listing-field' }}
                firstOption={
                  {
                    id: 'show-listing-true',
                    value: 'true',
                    label: intl.formatMessage(translationMessages.yes),
                    attributes: { 'data-testing-id': 'show-listing-true' }
                  }}
                secondOption={
                  {
                    id: 'show-listing-false',
                    value: 'false',
                    label: intl.formatMessage(translationMessages.no),
                    attributes: { 'data-testing-id': 'show-listing-false' }
                  }
                }
              />
              <p className="text-small text-gray-600 margin-lg-top">
                <FormattedMessage
                  id="hosting.create_item_form.showing_on_listing_text"
                  defaultMessage="Showing this item on your listing will make it available for purchase. "/>
                {!fromManageAddons && <FormattedMessage id="hosting.create_item_form.manage_addons_text" defaultMessage="You can manage your add-ons from the Add-on Inventory page."/>}
              </p>
            </div>

            <hr className="no-bottom-margin" />
          </form>
      }
    </div>
  );
};

CreateItemForm.propTypes = {
  currency: PropTypes.string,
  addOnFeeValues: PropTypes.array,
  handleCategoryChange: PropTypes.func,
  categoryOptions: PropTypes.array,
  priceOptions: PropTypes.array,
  subcategoryOptions: PropTypes.array,
  handleSubmit: PropTypes.func,
  isFetching: PropTypes.bool,
  fromManageAddons: PropTypes.bool,
  handlePhotoChange: PropTypes.func,
  addOnImgId: PropTypes.string,
  isEditAddOnModal: PropTypes.bool,
  handleRemovePhotoClick: PropTypes.func,
  iosWebView: PropTypes.bool,
  intl: intlShape
};

const config = {
  form: CREATE_ITEM_FORM,
  onSubmit: submitCreateAddOnForm,
  validate: validateCreateItemForm
};

const mapStateToProps = (state) => {
  return {
    formValues: get(state, `form[${CREATE_ITEM_FORM}].values`, {}),
    error: get(state, `form[${CREATE_ITEM_FORM}].error`)
  };
};

export default reduxForm(config)(injectIntl(connect(mapStateToProps)(CreateItemForm)));
